import { LitElement, html, css, utils, styles, t } from '@hh/global.js'
const { addToCart, reRenderShopifySections, randomFixedInteger } = utils
class HHSamplerSetBuilder extends LitElement {
  static properties = {
    full: {
      reflect: true,
    },
    selected: {
      reflect: true,
    },
    empty: {
      reflect: true,
    },
    _trapFocus: {
      state: true,
    },
    limit: {
      type: Number,
    },
    sectionId: {
      attribute: 'section-id',
      type: String,
    },
    parentVariantId: {
      attribute: 'parent-variant-id',
      type: Number,
    },
    parentVariantPrice: {
      attribute: 'parent-variant-price',
      type: Number,
    },
  }

  constructor() {
    super()

    this.items = []
    this.productDetails = []
    this.totalPrice = 0
    this.submitButton = this.querySelector('[type="submit"]')
    this.noteAddText = this.querySelectorAll('.js-add-text')
    this.parentMetafields = JSON.parse(this.querySelector('#variantMetafields').textContent)

    this.placeholderContainer = this.querySelector(
      '.js-sampler-placeholder-container'
    )
    this.placeholder = this.placeholderContainer.querySelector(
      '.js-sampler-placeholder'
    )
    this.placeholderAll = this.placeholderContainer.querySelectorAll(
      '.js-sampler-placeholder'
    )

    this.listContainer = this.querySelector('.js-sampler-list-container')
    this.list = this.listContainer.querySelector('.js-sampler-list')
    this.listAll = this.listContainer.querySelectorAll('.js-sampler-list')
  }

  connectedCallback() {
    super.connectedCallback()

    this.setMinimumPrice()
    this.addEventListener('handleSample', this.selectSample.bind(this))
    this.addEventListener('handleSamples', this.handleSamples.bind(this))

    window.addEventListener('variant:change', this.onVariantChange.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.removeEventListener('handleSample', this.selectSample)
    this.removeEventListener('handleSamples', this.handleSamples)
  }

  selectSample(event) {
    event.stopImmediatePropagation()

    const { itemImage, itemName, itemDesc, itemProperties } = event.target.dataset
    const id = event.detail.key
    const addText = event.srcElement.parentElement
      .querySelector('.js-add-text')
      .innerHTML.replace(/[()]/g, '')
      .replace(/\+/g, '+ ')
    const placeholderContainer = this.placeholderContainer.querySelectorAll(
      '.js-sampler-placeholder'
    )
    const listContainer =
      this.listContainer.querySelectorAll('.js-sampler-list')
    const isSelected = this.items.includes(+id)
    const keys = this.querySelectorAll(`[key="${id}"]`)

    if (isSelected) {
      this.items = this.items.filter((item) => +item !== +id)
      this.productDetails = this.productDetails.filter(
        (item) => +item.id !== +id
      )

      keys.forEach((element) => {
        element.closest('.js-select-sample')?.classList.remove('selected')
      })

      this.removePlaceholder(placeholderContainer, this.items.length)
      this.removePlaceholder(listContainer, this.items.length)
    } else {
      if (this.items.length === this.limit) return
      this.items.push(+id)
      this.productDetails.push({
        id,
        itemImage,
        itemName: `${itemName} ${addText}`,
        itemDesc,
        itemProperties
      })

      keys.forEach((element) => {
        element.closest('.js-select-sample')?.classList.add('selected')
      })

      this.updatePlaceholder(placeholderContainer, this.items.length)
      this.updatePlaceholder(listContainer, this.items.length)
    }

    this.full = this.limit === this.items.length
    this.selected = this.items.length > 0
    this.empty = this.items.length === 0

    this.selected ? this.setSelectedPrice() : this.setMinimumPrice()

    const btnText = this.full
      ? `${window.variantStrings.addToCart} - ${HH.formatMoney(
        this.totalPrice + this.parentVariantPrice,
        window.shopSettings.money_with_currency_format
      )}`
      : window.variantStrings.makeSelection

    this.submitButton.textContent = btnText
    this.submitButton.text = btnText
    this.updateSelectionCount.bind(this)(this.items.length, this.limit)
  }

  removePlaceholder(placeholders, i) {
    const placeholderContainer = this.placeholderContainer.querySelectorAll(
      '.js-sampler-placeholder'
    )
    const listContainer =
      this.listContainer.querySelectorAll('.js-sampler-list')

    const image = placeholders[i].querySelector('.js-sample-image')
    const title = placeholders[i].querySelector('.js-sample-title')
    const desc = placeholders[i].querySelector('.js-sample-desc')
    placeholders[i].classList.remove('hide-placeholder')
    if (image) {
      image.style.backgroundImage = ''
    }
    if (title) {
      title.innerHTML = ''
    }
    if (desc) {
      desc.innerHTML = ''
    }
    this.updatePlaceholder(placeholderContainer, this.items.length)
    this.updatePlaceholder(listContainer, this.items.length)
  }

  clearPlaceholder(placeholders) {
    placeholders.forEach((placeholder) => {
      const image = placeholder.querySelector('.js-sample-image')
      const title = placeholder.querySelector('.js-sample-title')
      const desc = placeholder.querySelector('.js-sample-desc')
      placeholder.classList.remove('hide-placeholder')
      if (image) {
        image.style.backgroundImage = ''
      }
      if (title) {
        title.innerHTML = ''
      }
      if (desc) {
        desc.innerHTML = ''
      }
    })
  }

  updatePlaceholder(placeholders, i) {
    placeholders.forEach((placeholder, index) => {
      if (index + 1 <= i) {
        const { id, itemImage, itemName, itemDesc } = this.productDetails[index]
        const image = placeholder.querySelector('.js-sample-image')
        const title = placeholder.querySelector('.js-sample-title')
        const desc = placeholder.querySelector('.js-sample-desc')
        const removeButton = placeholder.querySelector(
          '.js-sample-remove-button'
        )

        placeholder.classList.add('hide-placeholder')

        if (image) {
          image.style.backgroundImage = `url(${itemImage})`
        }
        if (title) {
          title.innerHTML = itemName
        }
        if (desc) {
          desc.innerHTML = itemDesc
        }
        if (removeButton) {
          removeButton.setAttribute('key', id)
        }
      }
    })
  }

  updateSelectionCount(count, limit) {
    this.querySelector('[data-selection-limit]').innerHTML = limit
    this.querySelector('[data-selection-count]').innerHTML = count
  }

  async handleSamples() {
    document.activeElement.blur()
    const uuid = crypto.randomUUID()
    const itemKey = randomFixedInteger(14)
    const parent_id = Number(this.parentVariantId) || 0
    const submitButton = this.submitButton
    const minPrice = this.getMinimumPrice()
    const samples = {}
    const parentMetafields = this.parentMetafields.find(metafield => metafield.id === parent_id)

    this.productDetails.forEach(({ itemName }, index) => {
      samples[
        `${t('products.product.sampler_set.sample_property')} ${index + 1}`
      ] = itemName
    })

    submitButton.setAttribute('disabled', true)

    const items = this.items.map((id) => {
      const item = this.productDetails.find(detail => +detail.id === +id)
      return {
        id,
        quantity: 1,
        properties: Object.assign({
          _group_id: uuid,
          '_attached-item-key': itemKey,
          '_is-sampler-set': true,
          '_parent-id': parent_id,
          '_is-hidden': true,
        }, !!item.itemProperties ?
          JSON.parse(item.itemProperties)
        : {})
      }
    })

    items.push({
      id: parent_id,
      quantity: 1,
      properties: Object.assign({
        _is_parent: true,
        _group_id: uuid,
        '_attached-item-key': itemKey,
        '_is-sampler-set': true,
        '_sample-min-price': minPrice,
        ...samples,
      }, !!parentMetafields.lineProperties ?
        (typeof parentMetafields.lineProperties === 'string' ? JSON.parse(parentMetafields.lineProperties) : parentMetafields.lineProperties)
      : {}),
    })

    if (items.length > 0) {
      reRenderShopifySections(await addToCart({ items }))

      this.openCart()
    }

    submitButton.removeAttribute('disabled')
    document.documentElement.classList.remove('overflow-hidden')
  }

  onVariantChange(e) {
    const { variant } = e.detail
    this.parentVariantPrice = variant.price
    this.parentVariantId = variant.id
    this.limit = parseFloat(variant.option1)
    this.updateSamplerSection()
    this.setMinimumPrice()
    this.updateAddText()
  }

  updateSamplerSection() {
    this.items = []
    this.productDetails = []
    this.selected = false
    this.empty = true
    this.full = false

    this.updateSelectionCount.bind(this)(0, this.limit)
    this.clearSampleSelection.bind(this)()
    this.populatePlaceholder.bind(this)(
      this.placeholderContainer,
      this.placeholder
    )
    this.populatePlaceholder.bind(this)(this.listContainer, this.list)
  }

  clearSampleSelection() {
    this.querySelectorAll('.js-select-sample').forEach((element) => {
      element.classList.remove('selected')
    })
    this.clearPlaceholder(this.placeholderAll)
    this.clearPlaceholder(this.listAll)
  }

  populatePlaceholder(container, node) {
    container.innerHTML = ''

    for (let i = 1; i <= this.limit; i++) {
      container.appendChild(node.cloneNode(true))
    }
  }

  updateAddText() {
    const price = this.getMinimumPrice()

    this.noteAddText.forEach((note) => {
      const notesPrice = note.dataset.price
      const hasAdd = notesPrice > price

      const addText = hasAdd
        ? `(+${HH.formatMoney(
          notesPrice - price,
          window.shopSettings.money_with_currency_format
        )})`
        : ''

      note.innerHTML = addText
    })
  }

  getSamplerSetCollectionData() {
    this.samplerSetCollectionData =
      this.samplerSetCollectionData ||
      JSON.parse(document.querySelector('#samplerSetCollection').textContent)
    return this.samplerSetCollectionData
  }

  getMinimumPrice() {
    const samples = this.getSamplerSetCollectionData()
    samples.sort((a, b) => a.price - b.price)

    return samples[this.limit - 1].price
  }

  setMinimumPrice() {
    const samples = this.getSamplerSetCollectionData()

    samples.sort((a, b) => a.price - b.price)

    this.totalPrice = samples
      .slice(0, this.limit)
      .reduce((total, { price }) => total + price, 0)

    document
      .querySelectorAll(`.js-price-${this.sectionId}`)
      .forEach((element) => {
        element.innerHTML = `From ${HH.formatMoney(
          this.totalPrice + this.parentVariantPrice,
          window.shopSettings.money_with_currency_format
        )}`
      })
  }

  setSelectedPrice() {
    const samples = this.getSamplerSetCollectionData()

    this.totalPrice = samples.reduce(
      (total, sample) =>
        this.items.includes(sample.variantId) ? total + sample.price : total,
      0
    )

    document
      .querySelectorAll(`.js-price-${this.sectionId}`)
      .forEach((element) => {
        element.innerHTML = HH.formatMoney(
          this.totalPrice + this.parentVariantPrice,
          window.shopSettings.money_with_currency_format
        )
      })
  }

  openCart() {
    document.body.classList.add('mini-cart-is-visible')

    setTimeout(() => {
      document.querySelector('html').classList.add('overflow-hidden')
      document.body.classList.add('mini-cart-is-active')
      document.querySelector('.js-mega-menu-overlay').classList.add('active')
    });
  }

  render() {
    return html`<div>
      <slot name="selections"></slot>
    </div> `
  }

  static styles = [styles.visuallyHidden, css``]
}

customElements.define('hh-sampler-set-builder', HHSamplerSetBuilder)
